import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { ABOUT_TO_START, TEST_STATUS } from '../load_test_components/steps';
import { fetchGPSData, fetchGpsSystems, fetchSurveyGps, fetchProjects, fetchLoadTestBoxes, fetchBusinessUnits } from '../services/api';

// Operation-map layers. Offices and Real Estate are still to come — no table
// holds their coordinates yet (see the to-do list).
const LAYERS = [
  { key: 'rigs', label: 'Rigs', color: '#0ea5e9' },
  { key: 'emlid', label: 'Emlid', color: '#7c3aed' },
  { key: 'jobSites', label: 'Job Sites', color: '#16a34a' },
  { key: 'testBoxes', label: 'Test Boxes', color: '#d97706' },
  { key: 'loadTest', label: 'Load Test', color: '#dc2626' },
];
const LAYER_BY_KEY = Object.fromEntries(LAYERS.map(l => [l.key, l]));
// Drawn bottom to top, so an active load test sits above its own test box dot.
const DRAW_ORDER = ['jobSites', 'rigs', 'emlid', 'testBoxes', 'loadTest'];

const DEFAULT_VIEW = { center: [39.5, -98.35], zoom: 4 };
const COUNTRY_VIEW = {
  'United States': { center: [39.5, -98.35], zoom: 4 },
  'Canada': { center: [56.13, -106.35], zoom: 3 },
  'France': { center: [46.6, 2.2], zoom: 5 },
  'United Kingdom': { center: [54.5, -3.0], zoom: 5 },
};

// Space kept clear under the map so it ends just above the fixed footer:
// MainLayout's FOOTER_HEIGHT (56) plus the main area's bottom padding (24).
const MAP_BOTTOM_CLEARANCE = 56 + 24;
const MAP_MIN_HEIGHT = 360;

const toNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const hasPos = (p) => p.lat != null && p.lon != null;
const buOrNull = (v) => (typeof v === 'string' && v.trim()) || null;

// Business unit filter sentinels (?bu=); real BU names never collide with these.
const ALL_BUS = '__all__';
const NO_BU = '__none__';

// Parse ?layers=a,b into a Set; no param means every layer on.
function layersFromParam(param) {
  if (!param) return new Set(LAYERS.map(l => l.key));
  return new Set(param.split(',').filter(k => LAYER_BY_KEY[k]));
}

// Frames the visible points once data arrives (or jumps to ?focus= when given)
// and opens the focused point's popup. Runs once per load so toggling layers
// afterwards doesn't yank the view around.
function FrameOnLoad({ points, focusId, markerRefs, ready }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    const focus = focusId && points.find(p => p.id === focusId);
    if (focus) {
      map.setView([focus.lat, focus.lon], 12);
      // The marker mounts in the same commit; open its popup on the next tick.
      setTimeout(() => markerRefs.current[focus.id]?.openPopup(), 0);
      return;
    }
    framePoints(map, points);
  }, [ready, points, focusId, map, markerRefs]);
  return null;
}

function framePoints(map, points, fallback) {
  if (points.length === 1) map.setView([points[0].lat, points[0].lon], 10);
  else if (points.length > 1) map.fitBounds(points.map(p => [p.lat, p.lon]), { padding: [40, 40], maxZoom: 12 });
  else if (fallback) map.setView(fallback.center, fallback.zoom);
}

// ZoomOutMap glyph from MUI icons, drawn into a plain Leaflet control.
const RESET_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6z"/></svg>';

// Reset-zoom button stacked under Leaflet's +/- control. Re-frames whatever
// layers are on now, or the country view when none have points.
function ResetZoomControl({ points, fallback }) {
  const map = useMap();
  const latest = useRef({ points, fallback });
  latest.current = { points, fallback };
  useEffect(() => {
    const Control = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const bar = L.DomUtil.create('div', 'leaflet-bar');
        const link = L.DomUtil.create('a', '', bar);
        link.href = '#';
        link.title = 'Reset zoom';
        link.setAttribute('role', 'button');
        link.setAttribute('aria-label', 'Reset zoom');
        link.style.display = 'flex';
        link.style.alignItems = 'center';
        link.style.justifyContent = 'center';
        link.innerHTML = RESET_ICON;
        L.DomEvent.disableClickPropagation(bar);
        L.DomEvent.on(link, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          framePoints(map, latest.current.points, latest.current.fallback);
        });
        return bar;
      },
    });
    const control = new Control().addTo(map);
    return () => control.remove();
  }, [map]);
  return null;
}

// Leaflet only re-measures on window resize; the fit-to-screen height changes
// the container directly, so invalidate on any container size change.
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function PopupRow({ label, value }) {
  if (value == null || value === '') return null;
  return <><br /><span style={{ opacity: 0.75 }}>{label}:</span> {value}</>;
}

function EquipmentMapPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const activeLayers = useMemo(() => layersFromParam(searchParams.get('layers')), [searchParams]);
  const focusId = searchParams.get('focus');
  const buFilter = searchParams.get('bu') || ALL_BUS;

  const [points, setPoints] = useState({ rigs: [], emlid: [], jobSites: [], testBoxes: [], loadTest: [] });
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const markerRefs = useRef({});
  // Every business unit for the account's country (same list as the add/edit
  // dialogs), so the filter offers all of them even when one has nothing mapped.
  const [allBusinessUnits, setAllBusinessUnits] = useState([]);
  useEffect(() => {
    fetchBusinessUnits(user?.country)
      .then(d => setAllBusinessUnits(d.businessUnits || []))
      .catch(() => setAllBusinessUnits([]));
  }, [user?.country]);

  useEffect(() => {
    // Each source loads independently: one failing (e.g. Samsara down) still
    // leaves the other layers on the map.
    const settle = (promise, label) => promise.catch((err) => {
      console.error(`Equipment map: ${label} failed`, err);
      setFailed(prev => [...prev, label]);
      return null;
    });
    Promise.all([
      settle(fetchGPSData(), 'Rigs'),
      // The live GPS feed has no BU; the GPS Systems records carry it.
      fetchGpsSystems().catch(() => null),
      settle(fetchSurveyGps(), 'Emlid'),
      settle(fetchProjects(), 'Job Sites'),
      settle(fetchLoadTestBoxes(), 'Test Boxes'),
    ]).then(([gps, gpsSystems, rovers, projects, boxes]) => {
      const rigBu = Object.fromEntries((gpsSystems?.records || []).map(r => [r.gps_number, buOrNull(r.biz_unit)]));
      const rigs = (gps?.results || []).map(d => ({
        id: `rig-${d.gps_num}`,
        lat: toNum(d.lat), lon: toNum(d.lon),
        bu: rigBu[d.gps_num] || null,
        title: d.machine || d.gps_num,
        rows: [['GPS', d.gps_num], ['Business Unit', rigBu[d.gps_num]], ['Status', d.online ? 'Online' : 'Offline'], ['Last update', d.last_update]],
      })).filter(hasPos);

      const emlid = (rovers?.rovers || []).map(r => {
        // Prefer the live Samsara fix; fall back to the position saved on the rover.
        const tl = r.tracker_location;
        const lat = toNum(tl?.lat) ?? toNum(r.latitude);
        const lon = toNum(tl?.lon) ?? toNum(r.longitude);
        return {
          id: `emlid-${r.id}`, lat, lon,
          bu: buOrNull(r.biz_unit),
          title: r.menard_internal_number || r.serial_number,
          rows: [['Model', r.model], ['Business Unit', r.biz_unit], ['Region', r.region], ['Location', tl?.city_state]],
        };
      }).filter(hasPos);

      const projectList = projects?.projects || [];
      const jobSites = projectList
        .filter(p => p.status === 'Current')
        .map(p => ({
          id: `job-${p.id}`,
          lat: toNum(p.latitude), lon: toNum(p.longitude),
          // Resolved server-side from region, region id or Salesforce territory.
          bu: buOrNull(p.businessUnit),
          title: p.projectName,
          rows: [['Project', p.projectNumber], ['Business Unit', p.businessUnit], ['Technique', p.primaryTechnique], ['Location', [p.city, p.state].filter(Boolean).join(', ')]],
        })).filter(hasPos);

      const projectByNumber = Object.fromEntries(projectList.map(p => [p.projectNumber, p]));
      const boxList = Array.isArray(boxes) ? boxes : [];
      const boxPoint = (b, idPrefix) => {
        const tl = b.tracker_location;
        return {
          id: `${idPrefix}-${b.id}`,
          lat: toNum(tl?.lat), lon: toNum(tl?.lon),
          bu: buOrNull(b.biz_unit),
          title: b.name || `Test Box ${b.id}`,
          rows: [['Business Unit', b.biz_unit], ['Logger', b.logger?.name], ['Location', tl?.city_state], ['Project', b.JobNumber],
            ...(b.about_to_start ? [['Status', ABOUT_TO_START.label]] : []),
            ...(b.test_running ? [['Test', TEST_STATUS[b.test_status]?.label || b.test_status]] : [])],
          // Test box dots turn purple when a test is about to start there.
          color: idPrefix === 'box' && b.about_to_start ? ABOUT_TO_START.color : undefined,
        };
      };
      const testBoxes = boxList.map(b => boxPoint(b, 'box')).filter(hasPos);
      // A load test is running when the box's latest test is (by the load: from
      // the first load step until the hour at zero confirms the end). Place it at
      // the tracker, or at its project's site when the tracker has no fix.
      const loadTest = boxList
        .filter(b => b.test_running)
        .map(b => {
          const pt = boxPoint(b, 'loadtest');
          if (!hasPos(pt)) {
            const proj = projectByNumber[b.ProjectNumber];
            pt.lat = toNum(proj?.latitude);
            pt.lon = toNum(proj?.longitude);
          }
          return pt;
        }).filter(hasPos);

      setPoints({ rigs, emlid, jobSites, testBoxes, loadTest });
      setLoading(false);
    });
  }, []);

  // Layers, BU and focus all live in the URL so a filtered view can be linked.
  const updateParams = (changes) => {
    const params = Object.fromEntries(searchParams.entries());
    Object.entries(changes).forEach(([k, v]) => {
      if (v == null) delete params[k]; else params[k] = v;
    });
    setSearchParams(params, { replace: true });
  };

  const toggleLayer = (key) => {
    const next = new Set(activeLayers);
    if (next.has(key)) next.delete(key); else next.add(key);
    updateParams({ layers: LAYERS.map(l => l.key).filter(k => next.has(k)).join(',') });
  };

  const matchesBu = (p) =>
    buFilter === ALL_BUS || (buFilter === NO_BU ? !p.bu : p.bu === buFilter);

  // Per-layer points after the BU filter; chip counts follow it too.
  const filteredPoints = useMemo(
    () => Object.fromEntries(Object.entries(points).map(([k, list]) => [k, list.filter(matchesBu)])),
    [points, buFilter]
  );

  // The full BU list, plus any BU found on a mapped item that isn't in it.
  const buOptions = useMemo(() => {
    const all = Object.values(points).flat();
    return {
      names: [...new Set([...allBusinessUnits, ...all.map(p => p.bu).filter(Boolean)])].sort(),
      hasNone: all.some(p => !p.bu),
    };
  }, [points, allBusinessUnits]);

  const visiblePoints = useMemo(
    () => DRAW_ORDER.filter(k => activeLayers.has(k)).flatMap(k => filteredPoints[k].map(p => ({ ...p, layer: k }))),
    [filteredPoints, activeLayers]
  );

  // Fit the map between its top edge and the footer (same approach as the
  // Coverage Map), re-measured on resize.
  const mapRef = useRef(null);
  const [mapHeight, setMapHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      const el = mapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setMapHeight(Math.max(MAP_MIN_HEIGHT, Math.round(window.innerHeight - top - MAP_BOTTOM_CLEARANCE)));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, []);

  const view = COUNTRY_VIEW[user?.country] || DEFAULT_VIEW;

  return (
    // Bottom padding = the footer clearance, so on a short window (map at its
    // minimum height) the page can scroll the map out from under the footer.
    <Box sx={{ pb: `${MAP_BOTTOM_CLEARANCE}px` }}>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {/* Title and the BU filter share a row on desktop to save map height */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={700}>Equipment Map</Typography>
            <Typography variant="body2" color="text.secondary">
              Toggle layers to show where rigs, rovers, job sites and load test boxes are.
              {failed.length > 0 && <Box component="span" sx={{ color: 'error.main', ml: 1 }}>Couldn't load: {failed.join(', ')}</Box>}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0, bgcolor: 'background.paper' }}>
            <InputLabel>Business Unit</InputLabel>
            <Select
              value={buFilter}
              label="Business Unit"
              onChange={(e) => updateParams({ bu: e.target.value === ALL_BUS ? null : e.target.value })}
            >
              <MenuItem value={ALL_BUS}>All business units</MenuItem>
              {buOptions.names.map((bu) => <MenuItem key={bu} value={bu}>{bu}</MenuItem>)}
              {buOptions.hasNone && <MenuItem value={NO_BU}><em>No business unit</em></MenuItem>}
              {/* Keep a linked ?bu= value selectable even if no point carries it */}
              {![ALL_BUS, NO_BU, ...buOptions.names].includes(buFilter) && <MenuItem value={buFilter}>{buFilter}</MenuItem>}
            </Select>
          </FormControl>
        </Stack>

        {/* Layers, kept together as one group */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {LAYERS.map((layer) => {
            const on = activeLayers.has(layer.key);
            return (
              <Chip
                key={layer.key}
                onClick={() => toggleLayer(layer.key)}
                label={`${layer.label}${loading ? '' : ` (${filteredPoints[layer.key].length})`}`}
                icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: layer.color, ml: '8px !important', opacity: on ? 1 : 0.35 }} />}
                variant={on ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  bgcolor: on ? (isDark ? 'rgba(35,35,35,0.95)' : '#fff') : 'background.paper',
                  border: 2,
                  borderColor: on ? layer.color : 'divider',
                  color: on ? 'text.primary' : 'text.secondary',
                }}
              />
            );
          })}
          <Tooltip title="Offices and Real Estate need location data before they can be mapped" arrow>
            <Chip label="Offices · Real Estate (coming soon)" variant="outlined" disabled sx={{ fontWeight: 600 }} />
          </Tooltip>
        </Stack>
      </Stack>

      <Paper ref={mapRef} sx={{ position: 'relative', height: mapHeight ?? '70vh', minHeight: MAP_MIN_HEIGHT, borderRadius: 2, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.15)' }}>
            <CircularProgress />
          </Box>
        )}
        <MapContainer
          center={view.center}
          zoom={view.zoom}
          preferCanvas
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
          attributionControl={false}
        >
          <TileLayer
            url={isDark
              ? 'https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
              : 'https://tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
            }
          />
          <InvalidateOnResize />
          <ResetZoomControl points={visiblePoints} fallback={view} />
          <FrameOnLoad points={visiblePoints} focusId={focusId} markerRefs={markerRefs} ready={!loading} />
          {visiblePoints.map((p) => {
            const layer = LAYER_BY_KEY[p.layer];
            const isLoadTest = p.layer === 'loadTest';
            return (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lon]}
                radius={isLoadTest ? 10 : 7}
                ref={(el) => { markerRefs.current[p.id] = el; }}
                pathOptions={{ color: '#fff', weight: 2, fillColor: p.color || layer.color, fillOpacity: 0.9 }}
              >
                <Popup>
                  <div style={{ minWidth: 150 }}>
                    <span style={{ color: layer.color, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>{layer.label}</span>
                    <br /><strong>{p.title}</strong>
                    {p.rows.map(([label, value]) => <PopupRow key={label} label={label} value={value} />)}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Paper>
    </Box>
  );
}

export default EquipmentMapPage;
